import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { Modal, Field, label } from '../ui.jsx';
import { useConfirm, useToast } from '../feedback.jsx';
import { t } from '../../i18n/index.jsx';
import CompanySelect from '../CompanySelect.jsx';

export const INVOICE_TYPES = ['proforma', 'final'];
export const INVOICE_STATUSES = ['draft', 'sent', 'partially_paid', 'paid', 'overdue', 'cancelled'];
export const CURRENCIES = ['EUR', 'MAD', 'USD'];

export const blankInvoice = {
  type: 'proforma', status: 'draft', client_id: '', own_company_id: '', project_id: '',
  currency: 'EUR', issue_date: '', due_date: '', place: 'Casablanca', reference: '', attn_name: '',
  subtitle: '', base_amount: '', rate_pct: '', apply_tranche: false, tranche_pct: '', tranche_desc: '',
  description: '', base_label: '', number: '', tva_note: '', notes: '',
};

/** Mirrors InvoiceController::extractCmsRef so the live preview matches what the server will store. */
function extractCmsRef(ref) {
  const m = ref.match(/CMS\s*n[°o]?\s*([A-Z0-9\-/]+)/i);
  if (m) return `CMS ${m[1]}`;
  const m2 = ref.match(/(HRMR[-\d/]+)/i);
  if (m2) return `CMS ${m2[1]}`;
  return ref;
}

function fmtNum(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toFixed(2).replace(/\.?0+$/, '') || '0';
}

/** Client-side mirror of InvoiceController::compute/autoDescription/autoBaseLabel — instant feedback, server stays authoritative on save. */
function computePreview(form, projectName) {
  const base = parseFloat(form.base_amount) || 0;
  const rate = parseFloat(form.rate_pct) || 0;
  const tranchePct = form.apply_tranche ? parseFloat(form.tranche_pct) || 0 : 0;
  const amount = form.apply_tranche && tranchePct > 0
    ? base * (tranchePct / 100) * (rate / 100)
    : base * (rate / 100);

  const descParts = ['Commission Agevolami'];
  if (projectName) descParts.push(projectName);
  if (form.reference) descParts.push(extractCmsRef(form.reference));
  if (form.tranche_desc) descParts.push(form.tranche_desc);

  const baseLabel = form.apply_tranche && tranchePct > 0
    ? `${fmtNum(rate)}% sur ${fmtNum(tranchePct)}% de ${fmtNum(base)} ${form.currency} HT`
    : `${fmtNum(rate)}% sur ${fmtNum(base)} ${form.currency} HT`;

  // The sequence itself depends on what's already stored, so the hint shows
  // the shape only — the server fills in the real number when left blank.
  const year = (form.issue_date || '').slice(0, 4) || String(new Date().getFullYear());
  const numberHint = `AGV-${form.type === 'final' ? 'FT' : 'PF'}-###-${year}`;

  return { amount: Math.round((amount + Number.EPSILON) * 100) / 100, description: descParts.join(' — '), baseLabel, numberHint };
}

export default function InvoiceForm({ initial, recipients = [], ownCompanies = [], projects = [], onClose, onSaved }) {
  const confirm = useConfirm();
  const toast = useToast();
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...blankInvoice, ...initial });
  const [baseline] = useState(() => JSON.stringify({ ...blankInvoice, ...initial }));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setBool = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  function pickClient(clientId) {
    const company = recipients.find((c) => String(c.id) === String(clientId));
    setForm((f) => ({
      ...f,
      client_id: clientId,
      attn_name: f.attn_name || company?.contact_name || '',
    }));
  }

  const projectName = useMemo(
    () => projects.find((p) => String(p.id) === String(form.project_id))?.name || '',
    [projects, form.project_id]
  );
  const preview = useMemo(() => computePreview(form, projectName), [form, projectName]);

  async function save() {
    setBusy(true); setErrors({});
    try {
      // base_label is derived server-side; sending the stale value we loaded
      // would pin it to the old rate instead of the edited one.
      const { base_label: _derived, ...rest } = form;
      const payload = { ...rest, project_name: projectName };
      const res = isNew ? await api.post('/invoices', payload) : await api.put(`/invoices/${initial.id}`, payload);
      toast(isNew ? t('Invoice created') : t('Invoice saved'));
      onSaved(res);
    } catch (err) { setErrors(err.fields || { _: err.message }); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!(await confirm({ title: t('Delete invoice'), message: t('Delete this invoice and its payment history?'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/invoices/${initial.id}`);
    toast(t('Invoice deleted'));
    onSaved();
  }

  return (
    <Modal title={isNew ? t('New invoice') : t('Edit invoice')} onClose={onClose}
      dirty={JSON.stringify(form) !== baseline}
      footer={
        <div className="modal-actions">
          {!isNew && <button className="btn danger ghost" onClick={remove}>{t('Delete')}</button>}
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      }>
      {errors._ && <div className="alert error">{errors._}</div>}

      <div className="grid-2">
        <CompanySelect
          label={t('Bill to (client or manufacturer)')}
          companies={recipients}
          value={form.client_id}
          onChange={pickClient}
          error={errors.client_id}
        />
        <Field label={t('Our company (emitter)')}>
          <select value={form.own_company_id} onChange={set('own_company_id')}>
            <option value="">—</option>
            {ownCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      </div>

      <div className="grid-2">
        <Field label={t('Project (optional)')}>
          <select value={form.project_id} onChange={set('project_id')}>
            <option value="">—</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label={t('Attention of (optional)')}><input value={form.attn_name} onChange={set('attn_name')} /></Field>
      </div>

      <div className="grid-3">
        <Field label={t('Type')}><select value={form.type} onChange={set('type')}>{INVOICE_TYPES.map((ty) => <option key={ty} value={ty}>{label(ty)}</option>)}</select></Field>
        <Field label={t('Status')}><select value={form.status} onChange={set('status')}>{INVOICE_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</select></Field>
        <Field label={t('Currency')}><select value={form.currency} onChange={set('currency')}>{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
      </div>

      <div className="grid-3">
        <Field label={t('Issue date')} error={errors.issue_date}><input type="date" value={form.issue_date || ''} onChange={set('issue_date')} /></Field>
        <Field label={t('Due date (optional)')} error={errors.due_date}><input type="date" value={form.due_date || ''} onChange={set('due_date')} /></Field>
        <Field label={t('Place')}><input value={form.place} onChange={set('place')} /></Field>
      </div>

      <Field label={t('Reference (purchase order, CMS ref…)')}><input value={form.reference} onChange={set('reference')} /></Field>
      <Field label={t('Subtitle (optional — printed under the title)')}><input value={form.subtitle} onChange={set('subtitle')} /></Field>

      <div className="grid-2">
        <Field label={t('Base amount HT')}><input type="number" step="0.01" value={form.base_amount} onChange={set('base_amount')} /></Field>
        <Field label={t('Commission rate %')}><input type="number" step="0.01" value={form.rate_pct} onChange={set('rate_pct')} /></Field>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 14 }}>
        <input type="checkbox" style={{ width: 'auto' }} checked={!!form.apply_tranche} onChange={setBool('apply_tranche')} />
        {t('Apply a tranche (partial commission on a % of the base amount)')}
      </label>
      {form.apply_tranche && (
        <div className="grid-2">
          <Field label={t('Tranche %')}><input type="number" step="0.01" value={form.tranche_pct} onChange={set('tranche_pct')} /></Field>
          <Field label={t('Tranche description')}><input value={form.tranche_desc} onChange={set('tranche_desc')} placeholder={t('e.g. Tranche 1 (30% deposit)')} /></Field>
        </div>
      )}

      <div className="commission-preview">
        {t('Amount')}: <b>{preview.amount.toLocaleString()} {form.currency}</b>
        <div className="muted sm" style={{ marginTop: 4 }}>{preview.baseLabel}</div>
      </div>

      <Field label={t('Description (optional — auto-generated if left blank)')}>
        <input value={form.description} onChange={set('description')} placeholder={preview.description} />
      </Field>
      <Field label={t('Number (optional — auto-generated if left blank)')}>
        <input value={form.number} onChange={set('number')} placeholder={preview.numberHint} />
      </Field>
      <Field label={t('VAT / legal note (optional)')}><textarea rows={2} value={form.tva_note} onChange={set('tva_note')} /></Field>
      <Field label={t('Internal notes (optional)')}><textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
    </Modal>
  );
}
